import React, {
  useRef,
  useState,
  useCallback,
  useEffect
} from "react";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@mui/icons-material';
import imageCompression from 'browser-image-compression';

import { defaultFunction, getAPIidentificationUrl } from "../../utilities/utilities";
import { useDetectionWorker } from "../../../contexts/DetectionWorkerContext";
import './IDDialog.sass';
import {
  DETECT_INTERVAL_MS,
  SUCCESS,
  WAITING,
  ERROR,
  UPDATE,
  EXTRACTION_RESULT,
  DETECTION_RESULT,
  DETECT,
  EXTRACT,
  EXTRACTION_ERROR,
  OPTIONS_IMAGE_COMPRESSION,
  ID_CAMERA_SCANNING
} from "../../../variables";
import IDDIalogGuide from "./IDDialogGuide";
import IDDialogLoadingInfo from "./IDDialogLoadingInfo";
import { api } from "../../../api/api";
import { connect } from "react-redux";

let lastDetectionTime = 0;

function IDDialog(props) {
  const {
    open = false,
    cameras = [],
    onClose = defaultFunction,
    setPhoto = defaultFunction,
    onChangDataByID = defaultFunction,
    photo = '',
    enabled_OCR_extraction = false,
    enabled_local_ocr = true,
    isOnline = true,
  } = props;

  const { worker, workerStatus, loadingMessage: initialLoadingMessage } = useDetectionWorker();
  const { t } = useTranslation();
  const [visit, setVisit] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [componentLoadingMessage, setComponentLoadingMessage] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [camera, setCamera] = useState('');

  const [allConditionsMet, setAllConditionsMet] = useState(WAITING);
  const [localCameras, setLocalCameras] = useState([]);
  const [cameraBlocked, setCameraBlocked] = useState(false);

  const canEnumerateCameras = typeof navigator.mediaDevices?.enumerateDevices === 'function';

  const videoRef = useRef(null);
  const previewBlobRef = useRef(null);
  const webcamIDRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!canEnumerateCameras) { setCameraBlocked(true); return; }

    const enumerateLocalCameras = () => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    };

    enumerateLocalCameras();

    navigator.mediaDevices.addEventListener('devicechange', enumerateLocalCameras);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateLocalCameras);
    };
  }, [open, canEnumerateCameras]);

  useEffect(() => {
    if (cameras.length > 0) setLocalCameras(cameras);
  }, [cameras]);

  // Poll while the list is empty or all labels are blank (permission not yet granted).
  // Self-stops once real labels are available — no need to close and reopen the dialog.
  useEffect(() => {
    if (!open || !canEnumerateCameras) return;
    if (localCameras.length > 0 && localCameras.some(c => c.label)) return;

    const timer = setTimeout(() => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    }, 1500);

    return () => clearTimeout(timer);
  }, [open, canEnumerateCameras, localCameras]);

  useEffect(() => {
    if (!open || localCameras.length === 0) return;
    const stored = localStorage.getItem(ID_CAMERA_SCANNING);
    const isValid = localCameras.some(c => c.deviceId === stored);
    setCamera(isValid ? stored : localCameras[0].deviceId);
  }, [open, localCameras]);

  const cleanInfo = useCallback((options = {}) => {
    const { keepPhoto = false } = options;
    if (!keepPhoto && photo && photo.startsWith('blob:')) {
      URL.revokeObjectURL(photo);
    }
    if (!keepPhoto) setPhoto('');
    setVisit({});
    setIsCapturing(false);
  }, [photo, setPhoto]);

  const onSave = useCallback((v = visit) => {
    cleanInfo({ keepPhoto: true });
    if (enabled_OCR_extraction && v.identification) onChangDataByID(v);
    onClose();
  }, [cleanInfo, onChangDataByID, onClose, enabled_OCR_extraction, visit]);

  const capturePhoto = useCallback(async () => {
    const video = webcamIDRef.current?.video;
    if (!video || video.readyState < 4 || isCapturing) return;

    setIsCapturing(true);
    setLoading(true);
    setLoadingMessage(t('Processing image...'));

    const docCanvas = document.createElement('canvas');
    docCanvas.width = video.videoWidth;
    docCanvas.height = video.videoHeight;
    const docCtx = docCanvas.getContext('2d');
    docCtx.drawImage(video, 0, 0);

    previewBlobRef.current = await new Promise(resolve => docCanvas.toBlob(resolve, 'image/jpeg', 0.95));
    if (previewBlobRef.current) {
      if (photo && photo.startsWith('blob:')) URL.revokeObjectURL(photo);
      setPhoto(URL.createObjectURL(previewBlobRef.current));
    }

    setLoading(false);
    setIsCapturing(false);
  }, [isCapturing, photo, setPhoto, t]);

  const runExtractionPipeline = useCallback(async (detectionBox = null) => {
    if (!videoRef.current || isCapturing || !worker) return;

    setIsCapturing(true);
    setLoading(true);
    setLoadingMessage(t('Processing image...'));

    const video = videoRef.current;
    const sourceX = detectionBox ? detectionBox.x1 : 0;
    const sourceY = detectionBox ? detectionBox.y1 : 0;
    const sourceWidth = detectionBox ? (detectionBox.x2 - detectionBox.x1) : video.videoWidth;
    const sourceHeight = detectionBox ? (detectionBox.y2 - detectionBox.y1) : video.videoHeight;

    if (sourceWidth <= 0 || sourceHeight <= 0) {
      setIsCapturing(false);
      setLoading(false);
      return;
    }

    const docCanvas = document.createElement('canvas');
    docCanvas.width = sourceWidth;
    docCanvas.height = sourceHeight;
    const docCtx = docCanvas.getContext('2d');
    docCtx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);

    const imageData = docCtx.getImageData(0, 0, sourceWidth, sourceHeight);

    previewBlobRef.current = await new Promise(resolve => docCanvas.toBlob(resolve, 'image/jpeg', 0.95));
    if (previewBlobRef.current) {
      if (photo && photo.startsWith('blob:')) URL.revokeObjectURL(photo);
      setPhoto(URL.createObjectURL(previewBlobRef.current));
    }

    if (!enabled_OCR_extraction) {
      setLoading(false);
      setIsCapturing(false);
      return;
    }

    worker.postMessage({
      type: EXTRACT,
      payload: {
        imageData
      }
    }, [imageData.data.buffer]);

  }, [isCapturing, photo, setPhoto, enabled_OCR_extraction, t, worker]);

  const setExtractionResult = useCallback(payload => {
    setVisit(payload);
    onSave(payload);
    setLoading(false);
    setIsCapturing(false);
  }, [onSave])

  const resetState = useCallback(() => {
    setLoading(false);
    setIsCapturing(false);
  }, [])

  const sendProcessedImage = useCallback(async (formData) => {
    setLoading(true);
    setLoadingMessage(t('Extracting in cloud...'));
    try {
      const API_ID_IDENTIFICATION_URL = getAPIidentificationUrl();
      const response = await api.post(API_ID_IDENTIFICATION_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { visit: visitData, success } = response.data;
      if (success) {
        setVisit(visitData);
        onSave(visitData);
      } else {
        setVisit({});
      }
    } catch (error) {
      console.error('Error enviando la imagen al servidor:', error);
      setVisit({});
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [onSave, t]);

  const runExtractionCloud = useCallback(async () => {
    try {
      setLoadingMessage(t('Local extraction failed, using cloud...'));
      const compressedBlob = await imageCompression(previewBlobRef.current, OPTIONS_IMAGE_COMPRESSION);
      const formData = new FormData();
      formData.append('idImage', compressedBlob, 'id_capture.jpg');
      await sendProcessedImage(formData);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [sendProcessedImage, t])

  // Local OCR disabled: capture a plain frame (no local model/worker involved)
  // and send it straight to the cloud extraction service, bypassing ONNX/Tesseract entirely.
  const captureAndSendToCloud = useCallback(async () => {
    const video = webcamIDRef.current?.video;
    if (!video || video.readyState < 4 || isCapturing) return;

    setIsCapturing(true);
    setLoading(true);
    setLoadingMessage(t('Processing image...'));

    const docCanvas = document.createElement('canvas');
    docCanvas.width = video.videoWidth;
    docCanvas.height = video.videoHeight;
    const docCtx = docCanvas.getContext('2d');
    docCtx.drawImage(video, 0, 0);

    previewBlobRef.current = await new Promise(resolve => docCanvas.toBlob(resolve, 'image/jpeg', 0.95));
    if (previewBlobRef.current) {
      if (photo && photo.startsWith('blob:')) URL.revokeObjectURL(photo);
      setPhoto(URL.createObjectURL(previewBlobRef.current));
    }

    await runExtractionCloud();
  }, [isCapturing, photo, setPhoto, t, runExtractionCloud]);

  const handleClose = () => {
    cleanInfo({ keepPhoto: true });
    onClose();
  }

  useEffect(() => {
    if (!open || !enabled_OCR_extraction || !enabled_local_ocr || workerStatus !== 'ready' || !worker) {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      return;
    }

    const runDetectionLoop = () => {
      const video = webcamIDRef.current?.video;
      
      if (!worker || isCapturing || !video || video.readyState < 4 || video.videoWidth === 0 || video.videoHeight === 0) {
        if (!isCapturing) {
            animationFrameIdRef.current = requestAnimationFrame(runDetectionLoop);
        }
        return;
      }

      const now = performance.now();
      if (now - lastDetectionTime > DETECT_INTERVAL_MS) {
        lastDetectionTime = now;
        videoRef.current = video;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = videoRef.current.videoWidth;
        tempCanvas.height = videoRef.current.videoHeight;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(videoRef.current, 0, 0, tempCanvas.width, tempCanvas.height);
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        worker.postMessage({ type: DETECT, payload: { imageData } }, [imageData.data.buffer]);
      }
      animationFrameIdRef.current = requestAnimationFrame(runDetectionLoop);
    };

    worker.onmessage = (event) => {
      const { type, payload } = event.data;
      switch (type) {
        case DETECTION_RESULT:
          const { allConditionsMet = WAITING, highConfidenceDetection = {} } = payload;
          setAllConditionsMet(allConditionsMet);
          if (allConditionsMet === SUCCESS) {
            console.log(allConditionsMet, highConfidenceDetection)
            runExtractionPipeline(highConfidenceDetection);
          }
          break;
        case UPDATE:
          setComponentLoadingMessage(t(payload));
          break;
        case EXTRACTION_RESULT:
          setExtractionResult(payload);
          break;
        case EXTRACTION_ERROR:
          // API fallback requires connectivity — skip silently when offline
          if (isOnline) {
            runExtractionCloud();
          } else {
            resetState();
          }
          break;
        case ERROR:
          console.error("Worker Error:", payload);
          resetState();
          animationFrameIdRef.current = requestAnimationFrame(runDetectionLoop);
          break;
        default:
          console.log('Mensaje de worker no manejado en IDDialog:', type);
          break;
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(runDetectionLoop);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      if (worker) {
        worker.onmessage = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, worker, workerStatus, enabled_OCR_extraction, enabled_local_ocr]);

  const handleCameraChange = (event) => {
    localStorage.setItem(ID_CAMERA_SCANNING, event.target.value)
    setCamera(event.target.value);
  }

  const photoResume = () => (
    <Stack gap={2} alignItems="center">
      <div className='webcam-wrapper-ID'>
        <img src={photo} alt={t("User identification")} />
      </div>
      {loading && (
        <Stack direction="row" gap={2} alignItems="center" sx={{ my: 1 }}>
          <CircularProgress size={24} />
          <Typography>{loadingMessage || t('Processing...')}</Typography>
        </Stack>
      )}
      <Stack gap={2} direction="row" justifyContent="center">
        <Button variant='outlined' onClick={cleanInfo} disabled={loading}>{t('Capture again')}</Button>
        <Button variant='contained' disabled={loading} onClick={() => onSave()}>
          {loading ? `${t("Processing")}...` : t("Continue")}
        </Button>
      </Stack>
    </Stack>
  );

  const getNewPhoto = () => cameraBlocked ? (
    <p style={{ color: 'var(--error, #d32f2f)', textAlign: 'center' }}>
      {t('Camera access requires a secure connection (HTTPS). Please contact your administrator.')}
    </p>
  ) : (
    <Stack gap={2} alignItems="center">
      <FormControl fullWidth>
        <InputLabel id="select-camera">{t('Camera')}</InputLabel>
        <Select
          labelId="select-camera"
          id="demo-simple-select"
          value={camera}
          label={t("Camera")}
          onChange={handleCameraChange}
          size="small"
        >
          {localCameras.map((cam) => <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>)}
        </Select>
      </FormControl>
      <div className="webcam-wrapper-ID webcam-container-with-guide" style={{ width: 450, height: 350 }}>
        <Webcam
          key={camera}
          ref={webcamIDRef}
          audio={false}
          width={450}
          height={350}
          screenshotFormat="image/jpeg"
          videoConstraints={{ width: { ideal: 1280 }, height: { ideal: 720 }, deviceId: camera ? { exact: camera } : undefined }}
          style={{ width: '100%', height: '100%' }}
        />
        {enabled_OCR_extraction && enabled_local_ocr
          ? <IDDIalogGuide status={allConditionsMet} />
          : <hr className="hr-animation" />}
        {enabled_OCR_extraction && enabled_local_ocr && (
          <IDDialogLoadingInfo 
            modelLoading={workerStatus === 'loading'} 
            isCapturing={isCapturing} 
            loadingMessage={workerStatus === 'loading' ? t(initialLoadingMessage) : componentLoadingMessage} 
          />
        )}
      </div>
      <Stack gap={2} direction="row" justifyContent="center">
        <Button
          variant='outlined'
          onClick={
            !enabled_OCR_extraction
              ? capturePhoto
              : enabled_local_ocr
                ? () => runExtractionPipeline()
                : captureAndSendToCloud
          }
        >
          {t("Capture Manually")}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Dialog
      className='visitor-dialog'
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack gap={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <h1>{t("Scan document")}</h1>
            <IconButton aria-label="close" onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack gap={2} alignItems="center">
            {photo ? photoResume() : getNewPhoto()}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = state => {
  return {
    cameras: state.utilities.cameras,
    isOnline: state.utilities.isOnline,
  }
}

export default connect(mapStateToProps)(IDDialog)
